/**
 * Has this device opened Comeback before?
 *
 * One synchronous bit, and it exists for a specific reason. The first render
 * cannot wait for IndexedDB (spec §15 — the Catch button is tappable before any
 * store read completes), so the app used to paint the Catch screen and *then*
 * navigate a new user into onboarding once the log had been replayed. That
 * produced a visible flash, and worse: a tap landing in that window had the
 * screen pulled out from under it.
 *
 * `localStorage` is synchronous, so the decision can be made before the first
 * paint instead of after it.
 *
 * **This is not derived state made authoritative** (architecture §2.4 rule 6).
 * It is a UI hint of exactly the kind the scratch store exists for — losing it
 * costs one unnecessary trip through a flow the user can leave in one tap, and
 * nothing else. The event log remains the only truth about whether onboarding
 * was completed.
 */

const SEEN_KEY = 'comeback.seen';

/**
 * True when we know this device has been here before.
 *
 * Returns **true** when storage is unavailable — a private-mode browser, or one
 * with site data blocked. Guessing "new user" there would hijack the screen of
 * someone who has used the app for weeks, every single time they opened it. The
 * cost of guessing the other way is that a genuinely new user lands on the Catch
 * screen instead of onboarding, which the setup line on that screen covers.
 */
export function seenBefore(): boolean {
  try {
    return localStorage.getItem(SEEN_KEY) === '1';
  } catch {
    return true;
  }
}

export function markSeen(): void {
  try {
    localStorage.setItem(SEEN_KEY, '1');
  } catch {
    /* Nothing to do. The fallback above is already the safe direction. */
  }
}
